import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ChevronLeft,
    ChevronRight,
    User,
    Users,
    GraduationCap,
    School,
    Mail,
    BookOpen,
    Atom,
    TestTube,
    Leaf,
    Calculator,
    Sprout,
    TrendingUp,
    Cross,
    Plus,
    Globe,
    Users as UsersIcon,
    Monitor,
    Heart,
    Briefcase,
    Palette,
    PenTool,
    Home,
    Shield,
    Microscope,
    FlaskConical,
    TreePine,
    Target,
    BarChart3,
    Book,
    Activity,
    Gamepad2,
    Zap,
    FileText,
    Building2,
    Coffee,
    Baby,
    X,
    Info,
    Link
} from "lucide-react";
import bg from "../../assets/first_bg.png";
import { API_BASE_URL } from "../../config/apiConfig";
import WelcomeModal from "../../components/welcomeMessage";
import { useNavigate } from "react-router-dom";

// Restores an interrupted signup near where it left off instead of forcing
// a restart from step 1 every time the onboarding page is reopened.
const getInitialStep = () => {
    const saved = parseInt(localStorage.getItem("onboarding_step"), 10);
    return saved >= 1 && saved <= 3 ? saved : 1;
};

const OnboardingPage = () => {
    const [currentStep, setCurrentStep] = useState(getInitialStep);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [subjectsByClass, setSubjectsByClass] = useState({});
    // Shown after a successful onboarding so the user can write down their role ID
    const [completionInfo, setCompletionInfo] = useState(null);

    // Subject icons mapping
    const subjectIcons = {
        "Physics": Atom,
        "Chemistry": TestTube,
        "Biology": Leaf,
        "Further Mathematics": Calculator,
        "Mathematics": Calculator,
        "Agricultural Science": Sprout,
        "Commerce": TrendingUp,
        "CRS": Cross,
        "Christian Religious Studies (CRS)": Cross,
        "English Language": PenTool,
        "English Studies": Book,
        "Basic Science": Microscope,
        "Basic Technology": Monitor,
        "Social Studies": Globe,
        "Civic Education": UsersIcon,
        "Computer Science": Monitor,
        "Physical and Health Education": Heart,
        "Physical and Health Education (PHE)": Heart,
        "Business Studies": Briefcase,
        "Creative Arts": Palette,
        "Home Economics": Home,
        "Security Education": Shield,
        "Literature": BookOpen,
        "Geography": Globe,
        "History": FileText,
        "Economics": BarChart3,
        "Government": Building2,
        "French": Coffee,
        "Arabic": Book,
        "Hausa": Book,
        "Igbo": Book,
        "Yoruba": Book,
        "Fine Arts": Palette,
        "Music": Activity,
        "Technical Drawing": Target,
        "Auto Mechanics": Gamepad2,
        "Electronics": Zap,
        "Building Construction": Building2,
        "Metal Work": Plus,
        "Wood Work": TreePine,
        "Food and Nutrition": Home,
        "Clothing and Textiles": Home
    };

    useEffect(() => {
        const fetchMyself = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const res = await fetch(`${API_BASE_URL}/api/users/me`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (!res.ok) return;

                const user = await res.json();
                setFormData((prev) => ({
                    ...prev,
                    accountType: user.accountType || "",
                }));
            } catch (error) {
                console.error("Error fetching /users/me:", error);
            }
        };

        fetchMyself();
    }, []);

    const getSubjectIcon = (subject) => subjectIcons[subject] || BookOpen;

    useEffect(() => {
        const fetchSubjectsByClass = async () => {
            setIsLoading(true);
            try {
                // Grouped server-side and not gated on individual question
                // review_status, so a subject's presence here is stable —
                // it no longer flickers in/out of the list as questions get
                // approved/unapproved over time.
                const response = await fetch(`${API_BASE_URL}/api/quiz/metadata`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch subjects");
                }

                const data = await response.json();
                setSubjectsByClass(data.subjectsByClass || {});
            } catch (error) {
                console.error("Error fetching subjects by class:", error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubjectsByClass();
    }, []);

    const [formData, setFormData] = useState({
        accountType: "",
        school: "",
        yearGroup: "",
        guardianEmail: "",
        parentEmail: "",
        subjects: [],
        children: [],
        students: [], // ← teacher's students
    });

    const accountTypes = [
        { id: "student", label: "Student", icon: User },
        { id: "parent", label: "Parent", icon: Users },
        { id: "teacher", label: "Teacher", icon: GraduationCap }
    ];

    const [schools, setSchools] = useState([]);
    const [filteredSchools, setFilteredSchools] = useState([]);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/schools`)
            .then((res) => {
                if (!res.ok) throw new Error(`Network error: ${res.status}`);
                return res.json();
            })
            .then((data) => {
                setSchools(Array.from(new Set(data.schools || [])).sort());
            })
            .catch(() => {
                setSchools([
                    "Government Secondary School, Kano",
                    "Federal Government College School",
                    "Capital Science Secondary School",
                    "Greenfield International Secondary School",
                    "Chrisland Senior School",
                    "Corona Secondary School",
                    "Lead British International Secondary School",
                    "British International School (Secondary)",
                    "Nigerian Turkish International Secondary School",
                    "Grange School Secondary Section",
                    "Faith Academy Secondary",
                    "Other",
                ]);
            });
    }, []);

    const yearGroups = Object.keys(subjectsByClass);

    const preferredOrder = ["SS3", "SS2", "SS1", "JSS3", "JSS2", "JSS1"];
    const sortedYearGroups = [...yearGroups].sort(
        (a, b) => preferredOrder.indexOf(a) - preferredOrder.indexOf(b)
    );

    // Dynamic subjects list for student/teacher self
    const filteredSubjects = formData.yearGroup
        ? subjectsByClass[formData.yearGroup] || []
        : [];

    // ─── Handlers ────────────────────────────────────────────────────────────

    const handleAccountTypeSelect = (type) => {
        setFormData({ ...formData, accountType: type });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubjectToggle = (subject) => {
        const updatedSubjects = formData.subjects.includes(subject)
            ? formData.subjects.filter(s => s !== subject)
            : [...formData.subjects, subject];
        setFormData({ ...formData, subjects: updatedSubjects });
    };

    // ─── Parent helpers ───────────────────────────────────────────────────────

    const handleChildInputChange = (index, field, value) => {
        const updatedChildren = [...formData.children];
        updatedChildren[index] = { ...updatedChildren[index], [field]: value };
        if (field === 'yearGroup') updatedChildren[index].subjects = [];
        setFormData({ ...formData, children: updatedChildren });
    };

    const handleChildSubjectToggle = (childIndex, subject) => {
        setFormData(prev => {
            const updatedChildren = prev.children.map((child, index) => {
                if (index !== childIndex) return child;
                const updatedSubjects = child.subjects ? [...child.subjects] : [];
                return updatedSubjects.includes(subject)
                    ? { ...child, subjects: updatedSubjects.filter(s => s !== subject) }
                    : { ...child, subjects: [...updatedSubjects, subject] };
            });
            return { ...prev, children: updatedChildren };
        });
    };

    const addChild = () => {
        setFormData({
            ...formData,
            children: [...formData.children, { name: '', school: '', yearGroup: '', subjects: [] }]
        });
    };

    const removeChild = (index) => {
        if (formData.children.length > 1) {
            setFormData({ ...formData, children: formData.children.filter((_, i) => i !== index) });
        }
    };

    // ─── Teacher / student helpers ────────────────────────────────────────────

    const handleStudentInputChange = (index, field, value) => {
        const updatedStudents = [...formData.students];
        updatedStudents[index] = { ...updatedStudents[index], [field]: value };
        if (field === 'yearGroup') updatedStudents[index].subjects = [];
        setFormData({ ...formData, students: updatedStudents });
    };

    const handleStudentSubjectToggle = (studentIndex, subject) => {
        setFormData(prev => {
            const updatedStudents = prev.students.map((student, index) => {
                if (index !== studentIndex) return student;
                const updatedSubjects = student.subjects ? [...student.subjects] : [];
                return updatedSubjects.includes(subject)
                    ? { ...student, subjects: updatedSubjects.filter(s => s !== subject) }
                    : { ...student, subjects: [...updatedSubjects, subject] };
            });
            return { ...prev, students: updatedStudents };
        });
    };

    const addStudent = () => {
        setFormData({
            ...formData,
            students: [...formData.students, { name: '', school: '', yearGroup: '', subjects: [] }]
        });
    };

    const removeStudent = (index) => {
        if (formData.students.length > 1) {
            setFormData({ ...formData, students: formData.students.filter((_, i) => i !== index) });
        }
    };

    // ─── Navigation ───────────────────────────────────────────────────────────

    const nextStep = () => {
        if (currentStep < 3 && !isTransitioning) {
            setIsTransitioning(true);
            setTimeout(() => {
                const next = currentStep + 1;
                setCurrentStep(next);
                localStorage.setItem("onboarding_step", String(next));
                setIsTransitioning(false);
            }, 300);
        }
    };

    const prevStep = () => {
        if (currentStep > 1 && !isTransitioning) {
            setIsTransitioning(true);
            setTimeout(() => {
                const prev = currentStep - 1;
                setCurrentStep(prev);
                localStorage.setItem("onboarding_step", String(prev));
                setIsTransitioning(false);
            }, 300);
        }
    };

    const navigate = useNavigate();

    // ─── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (isLoading) return; // prevent double-submit
        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) { setIsLoading(false); return; }

            const payload = Object.fromEntries(
                Object.entries(formData).filter(
                    ([_, value]) => value && value.toString().trim() !== ""
                )
            );

            payload.hasSeenModal = true;
            payload.onboardingCompleted = true;

            // If the student arrived via a teacher invite link, pass the code
            // so the backend links them atomically in the same request
            const pendingTeacherCode = localStorage.getItem("pending_teacher_code");
            if (pendingTeacherCode) {
                payload.teacher_code = pendingTeacherCode;
            }

            if (formData.accountType === "parent" && Array.isArray(formData.children)) {
                payload.children = formData.children.map(child => ({
                    ...child,
                    email: formData.parentEmail,
                }));
            }

            // Teachers send a `students` array — no email injection needed
            if (formData.accountType === "teacher" && Array.isArray(formData.students)) {
                payload.students = formData.students;
            }

            const res = await fetch(`${API_BASE_URL}/api/users/details`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await res.json();
                console.error("Failed to save user details:", err);
                toast.error(err.message || "Registration failed. Please try again.");
                setIsLoading(false);
                return;
            }

            const data = await res.json();
            localStorage.setItem("hasSeenModal", "true");
            localStorage.setItem("accountType", data.accountType);
            localStorage.removeItem("onboarding_step");

            // Role ID was generated by the backend during this onboarding step —
            // save it so the dashboards can display it without an extra API call.
            // IMPORTANT: pick based on the user's actual accountType — not first-non-null —
            // otherwise a teacher who once had a stale student_id would see TSA-* instead of TTA-*.
            const newRoleId =
                data.accountType === "teacher" ? data.teacher_id :
                data.accountType === "parent"  ? data.parent_id  :
                data.student_id;
            if (newRoleId) {
                localStorage.setItem("role_id", newRoleId);
            }

            // Only clear the pending teacher code if the backend confirmed it was linked.
            // If linking failed (e.g. migration not run), keep it — the next login will retry.
            if (pendingTeacherCode) {
                if (data.teacherLinked) {
                    localStorage.removeItem("pending_teacher_code");
                } else {
                    toast("You'll be linked to your teacher after your first login.");
                }
            }

            // Show the ID card so the user can write it down before continuing.
            setCompletionInfo({
                id: newRoleId,
                accountType: data.accountType,
                name: localStorage.getItem("fullName") || "",
            });
        } catch (error) {
            console.error("Error saving user details:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Validation ───────────────────────────────────────────────────────────

    const canProceedStep1 = formData.accountType !== "";

    const canProceedStep2 =
        formData.accountType === "parent"
            // Children are optional — can add from dashboard later.
            // If any are added, all must have a name and year group.
            ? formData.children.length === 0 ||
              formData.children.every(c => c.name.trim() !== "" && c.yearGroup !== "")
            : formData.accountType === "teacher"
                // Students are optional — teacher can use invite link instead.
                // If any are added, all must have a name and year group.
                ? formData.students.length === 0 ||
                  formData.students.every(s => s.name.trim() !== "" && s.yearGroup !== "")
                : formData.yearGroup !== "";

    const canProceedStep3 =
        formData.accountType === "parent"
            ? formData.children.every(child => child.subjects?.length > 0)
            : formData.accountType === "teacher"
                ? formData.students.every(student => student.subjects?.length > 0)
                : formData.subjects.length > 0;

    // ─── Shared subject-picker sub-component ─────────────────────────────────

    const SubjectGrid = ({ items, yearGroupKey, selectedSubjects, onToggle, isTransitioning }) => {
        const subjects = yearGroupKey ? subjectsByClass[yearGroupKey] || [] : [];
        return (
            <div className="subjects-scroll grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 max-h-80 overflow-y-auto pr-2">
                {subjects.map((subject, index) => {
                    const IconComponent = getSubjectIcon(subject);
                    const isSelected = selectedSubjects?.includes(subject);
                    return (
                        <button
                            key={subject}
                            type="button"
                            onClick={() => onToggle(subject)}
                            className={`subject-btn p-3 md:p-4 rounded-lg border flex flex-col items-center justify-center space-y-2 text-center ${isSelected
                                    ? 'border-white bg-white/20 text-white selected-subject'
                                    : 'border-white/30 text-white/70 hover:border-white/50 hover:bg-white/5'
                                }`}
                            style={{
                                animationDelay: `${index * 50}ms`,
                                animation: !isTransitioning
                                    ? `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`
                                    : 'none',
                            }}
                        >
                            <IconComponent className={`w-6 h-6 md:w-8 md:h-8 ${isSelected ? 'text-white' : 'text-white/70'}`} />
                            <span className={`text-xs md:text-sm font-medium leading-tight ${isSelected ? 'text-white' : 'text-white/80'}`}>
                                {subject}
                            </span>
                            {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                        </button>
                    );
                })}
            </div>
        );
    };

    const SubjectSummary = ({ subjects }) => (
        <div className="bg-white/10 rounded-lg p-3 md:p-4 border border-white/20 mt-3">
            <p className="text-white/90 text-sm md:text-base font-medium text-center">
                Selected: {subjects?.length || 0} subject{(subjects?.length || 0) !== 1 ? 's' : ''}
            </p>
            {subjects?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1 md:gap-2 justify-center">
                    {subjects.slice(0, 3).map((subject, i) => (
                        <span key={i} className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                            {subject.length > 15 ? `${subject.substring(0, 15)}...` : subject}
                        </span>
                    ))}
                    {subjects.length > 3 && (
                        <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                            +{subjects.length - 3} more
                        </span>
                    )}
                </div>
            )}
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    // ID card shown after a successful onboarding submission
    if (completionInfo) {
        const roleLabel =
            completionInfo.accountType === "teacher" ? "Teacher ID" :
            completionInfo.accountType === "parent"  ? "Parent ID"  : "Student ID";

        const goToDashboard = () => {
            const type = completionInfo.accountType;
            if (type === "teacher") navigate("/teacher-dashboard");
            else if (type === "parent") navigate("/parent-dashboard");
            else navigate("/dashboard");
        };

        return (
            <div
                className="flex justify-center items-center min-h-screen"
                style={{
                    backgroundImage: `url(${bg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#2148C0",
                }}
            >
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 md:p-10 max-w-md w-[90%] text-center">
                    <div className="w-16 h-16 bg-green-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">🎉</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                        You're all set{completionInfo.name ? `, ${completionInfo.name.split(" ")[0]}` : ""}!
                    </h2>
                    <p className="text-white/70 text-sm mb-6">
                        Your {completionInfo.accountType} account is ready on TeeChaa.
                    </p>

                    {completionInfo.id ? (
                        <>
                            <div className="bg-white/20 border-2 border-white/40 rounded-xl px-6 py-5 mb-3">
                                <p className="text-white/70 text-xs uppercase tracking-widest mb-1">Your {roleLabel}</p>
                                <p className="text-white font-mono text-3xl font-bold tracking-wider">
                                    {completionInfo.id}
                                </p>
                            </div>
                            <p className="text-yellow-200 text-xs mb-6 leading-relaxed">
                                Save this ID — you can use it to log in anytime along with your password.
                            </p>
                        </>
                    ) : (
                        <p className="text-white/70 text-sm mb-6">
                            Continue to your dashboard to get started.
                        </p>
                    )}

                    <button
                        onClick={goToDashboard}
                        className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition"
                    >
                        Got it, let's go!
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex justify-center items-center min-h-screen py-4"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: '#2148C0'
            }}
        >
            <style jsx>{`
                .step-content { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                .step-entering { opacity: 0; transform: translateX(30px) scale(0.95); }
                .step-active { opacity: 1; transform: translateX(0) scale(1); }
                .step-exiting { opacity: 0; transform: translateX(-30px) scale(0.95); }
                .progress-step { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; }
                .progress-step.active { transform: scale(1.1); }
                .progress-line { transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1); }
                .button-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; }
                .button-hover:hover { transform: scale(1.02); }
                .account-type-card { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; }
                .account-type-card:hover { transform: translateY(-4px) scale(1.02); box-shadow: 0 8px 25px rgba(0,0,0,0.15); }
                .subject-btn { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center; min-height: 80px; }
                .subject-btn:hover { transform: scale(1.03); box-shadow: 0 4px 15px rgba(255,255,255,0.2); }
                .form-container { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
                .subjects-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.3) transparent; }
                .subjects-scroll::-webkit-scrollbar { width: 6px; }
                .subjects-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 3px; }
                .subjects-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 3px; }
                .subjects-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.5); }
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .fade-in-up { animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1); }
                @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
                .selected-subject { animation: pulse 0.6s ease-in-out; }
            `}</style>

            <WelcomeModal />

            <div className="flex flex-col bg-transparent rounded-lg overflow-hidden w-[95%] max-w-4xl fade-in-up">
                {/* Header */}
                <div className="text-center mb-6 md:mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">TeeChaa</h2>
                    <p className="text-white/80 text-sm md:text-base">Create Your Account</p>

                    <div className="flex justify-center items-center mt-4 md:mt-6 space-x-2 md:space-x-4">
                        {[1, 2, 3].map((step) => (
                            <div key={step} className="flex items-center">
                                <div className={`progress-step w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-bold ${currentStep >= step ? 'bg-white text-[#2148C0] active' : 'bg-white/30 text-white'}`}>
                                    {step}
                                </div>
                                {step < 3 && (
                                    <div className={`progress-line w-8 md:w-12 h-1 mx-1 md:mx-2 ${currentStep > step ? 'bg-white' : 'bg-white/30'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step Content */}
                <div className="form-container bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-8 border border-white/20">
                    <div className={`step-content ${isTransitioning ? 'step-exiting' : 'step-active'}`}>

                        {/* ── Step 1: Account Type ── */}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-6">
                                    Choose Your Account Type
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {accountTypes.map(({ id, label, icon: Icon }, index) => (
                                        <button
                                            key={id}
                                            onClick={() => handleAccountTypeSelect(id)}
                                            className={`account-type-card p-4 md:p-6 rounded-lg border-2 ${formData.accountType === id ? 'border-white bg-white/20 text-white' : 'border-white/30 text-white/70 hover:border-white/50 hover:bg-white/10'}`}
                                            style={{
                                                animationDelay: `${index * 100}ms`,
                                                animation: !isTransitioning ? `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 100}ms both` : 'none'
                                            }}
                                        >
                                            <Icon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3" />
                                            <div className="text-base md:text-lg font-medium">{label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Step 2: School & Year Group ── */}
                        {currentStep === 2 && (
                            <div className="space-y-6">
                                {/* ── PARENT ── */}
                                {formData.accountType === 'parent' && (
                                    <div>
                                        {/* Info banner: adding children here is optional */}
                                        <div className="flex items-start gap-3 bg-white/10 border border-white/30 rounded-lg p-4 mb-5">
                                            <Info className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-white font-medium text-sm mb-1">Adding children here is optional</p>
                                                <p className="text-white/70 text-xs leading-relaxed">
                                                    You can skip this step and manage your children's accounts from your Parent Dashboard after registration. You'll be able to add, edit, and monitor each child's progress there at any time.
                                                </p>
                                                <div className="flex items-center gap-1 mt-2 text-white/60 text-xs">
                                                    <Baby className="w-3 h-3" />
                                                    <span>Or add them now using the button below</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="text-lg md:text-xl font-semibold text-white">
                                                <Baby className="w-5 h-5 md:w-6 md:h-6 inline mr-2" />
                                                Children Details
                                            </h3>
                                            <button onClick={addChild} className="flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all duration-300">
                                                <Plus className="w-4 h-4 mr-1" /> Add Child
                                            </button>
                                        </div>

                                        <div className="space-y-6 max-h-96 overflow-y-auto">
                                            {formData.children.map((child, index) => (
                                                <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/20">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="text-white font-medium">Child {index + 1}</h4>
                                                        {formData.children.length > 1 && (
                                                            <button onClick={() => removeChild(index)} className="text-red-400 hover:text-red-300 transition-colors">
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        <div>
                                                            <label className="block text-white mb-2 text-sm"><User className="w-4 h-4 inline mr-2" />Child's Name</label>
                                                            <input type="text" value={child.name} onChange={(e) => handleChildInputChange(index, 'name', e.target.value)} placeholder="Enter child's name" className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm" required />
                                                        </div>

                                                        <div style={{ position: "relative", zIndex: 10 }}>
                                                            <label className="block text-white mb-2 text-sm"><School className="w-4 h-4 inline mr-2" />School</label>
                                                            <div className="relative">
                                                                <input type="text" value={child.school || ""} onChange={(e) => { const v = e.target.value; handleChildInputChange(index, 'school', v); setFilteredSchools(schools.filter(s => s.toLowerCase().includes(v.toLowerCase()))); }} placeholder="Select or type school name" className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm" />
                                                                {child.school && filteredSchools.length > 0 && (
                                                                    <ul className="absolute left-0 right-0 bg-white text-gray-800 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1 z-50">
                                                                        {filteredSchools.map(school => <li key={school} onClick={() => { handleChildInputChange(index, 'school', school); setFilteredSchools([]); }} className="px-3 py-2 hover:bg-gray-200 cursor-pointer">{school}</li>)}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-white mb-2 text-sm"><GraduationCap className="w-4 h-4 inline mr-2" />Year Group</label>
                                                            <select value={child.yearGroup} onChange={(e) => handleChildInputChange(index, 'yearGroup', e.target.value)} className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm" required>
                                                                <option value="">Select Year</option>
                                                                {sortedYearGroups.map(year => <option key={year} value={year} className="text-gray-800">{year}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── TEACHER ── */}
                                {formData.accountType === 'teacher' && (
                                    <div>
                                        {/* Info banner: adding students here is optional */}
                                        <div className="flex items-start gap-3 bg-white/10 border border-white/30 rounded-lg p-4 mb-5">
                                            <Info className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-white font-medium text-sm mb-1">Adding students here is optional</p>
                                                <p className="text-white/70 text-xs leading-relaxed">
                                                    You can skip this step entirely. After registration, your Teacher Dashboard gives you a personal invite code and shareable link — students click it, sign up or log in, and are instantly added to your class. You can also add or remove students from the dashboard at any time.
                                                </p>
                                                <div className="flex items-center gap-1 mt-2 text-white/60 text-xs">
                                                    <Link className="w-3 h-3" />
                                                    <span>Prefer to add manually? Use the button below</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="text-lg md:text-xl font-semibold text-white">
                                                <Users className="w-5 h-5 md:w-6 md:h-6 inline mr-2" />
                                                Your Students
                                            </h3>
                                            <button onClick={addStudent} className="flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all duration-300">
                                                <Plus className="w-4 h-4 mr-1" /> Add Student
                                            </button>
                                        </div>

                                        {formData.students.length === 0 && (
                                            <div className="text-center py-8 border border-dashed border-white/30 rounded-lg">
                                                <GraduationCap className="w-10 h-10 text-white/40 mx-auto mb-3" />
                                                <p className="text-white/60 text-sm mb-4">No students added yet.</p>
                                                <button onClick={addStudent} className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all duration-300 text-sm">
                                                    <Plus className="w-4 h-4 inline mr-1" /> Add your first student
                                                </button>
                                            </div>
                                        )}

                                        <div className="space-y-6 max-h-96 overflow-y-auto">
                                            {formData.students.map((student, index) => (
                                                <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/20">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="text-white font-medium">Student {index + 1}</h4>
                                                        {formData.students.length > 1 && (
                                                            <button onClick={() => removeStudent(index)} className="text-red-400 hover:text-red-300 transition-colors">
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        {/* Student Name */}
                                                        <div>
                                                            <label className="block text-white mb-2 text-sm">
                                                                <User className="w-4 h-4 inline mr-2" />Student's Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={student.name}
                                                                onChange={(e) => handleStudentInputChange(index, 'name', e.target.value)}
                                                                placeholder="Enter student's name"
                                                                className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm"
                                                                required
                                                            />
                                                        </div>

                                                        {/* School */}
                                                        <div style={{ position: "relative", zIndex: 10 }}>
                                                            <label className="block text-white mb-2 text-sm">
                                                                <School className="w-4 h-4 inline mr-2" />School
                                                            </label>
                                                            <div className="relative">
                                                                <input
                                                                    type="text"
                                                                    value={student.school || ""}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        handleStudentInputChange(index, 'school', v);
                                                                        setFilteredSchools(schools.filter(s => s.toLowerCase().includes(v.toLowerCase())));
                                                                    }}
                                                                    placeholder="Select or type school name"
                                                                    className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm"
                                                                />
                                                                {student.school && filteredSchools.length > 0 && (
                                                                    <ul className="absolute left-0 right-0 bg-white text-gray-800 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1 z-50">
                                                                        {filteredSchools.map(school => (
                                                                            <li
                                                                                key={school}
                                                                                onClick={() => {
                                                                                    handleStudentInputChange(index, 'school', school);
                                                                                    setFilteredSchools([]);
                                                                                }}
                                                                                className="px-3 py-2 hover:bg-gray-200 cursor-pointer text-sm"
                                                                            >
                                                                                {school}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Year Group */}
                                                        <div>
                                                            <label className="block text-white mb-2 text-sm">
                                                                <GraduationCap className="w-4 h-4 inline mr-2" />Year Group
                                                            </label>
                                                            <select
                                                                value={student.yearGroup}
                                                                onChange={(e) => handleStudentInputChange(index, 'yearGroup', e.target.value)}
                                                                className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm"
                                                                required
                                                            >
                                                                <option value="">Select Year</option>
                                                                {sortedYearGroups.map(year => (
                                                                    <option key={year} value={year} className="text-gray-800">{year}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── STUDENT / default ── */}
                                {formData.accountType !== 'parent' && formData.accountType !== 'teacher' && (
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-6">
                                            School & Year Group Details
                                        </h3>
                                        <div className="space-y-4">
                                            {/* School */}
                                            <div style={{ position: "relative", zIndex: 10 }}>
                                                <label className="block text-white mb-2 text-sm md:text-base">
                                                    <School className="w-4 h-4 inline mr-2" />School
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={formData.school}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setFormData(p => ({ ...p, school: v }));
                                                            setFilteredSchools(schools.filter(s => s.toLowerCase().includes(v.toLowerCase())));
                                                        }}
                                                        placeholder="Select your school or type the full name if not available"
                                                        className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm md:text-base"
                                                    />
                                                    {filteredSchools.length > 0 && (
                                                        <ul className="absolute left-0 right-0 bg-white text-gray-800 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1 z-50">
                                                            {filteredSchools.map(school => (
                                                                <li key={school} onClick={() => { setFormData(p => ({ ...p, school })); setFilteredSchools([]); }} className="px-3 py-2 hover:bg-gray-200 cursor-pointer">
                                                                    {school}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>

                                            {formData.accountType === "student" && (
                                                <div>
                                                    <label className="block text-white mb-2 text-sm md:text-base">Guardian Email</label>
                                                    <input
                                                        type="email"
                                                        value={formData.guardianEmail || ""}
                                                        onChange={(e) => setFormData(p => ({ ...p, guardianEmail: e.target.value }))}
                                                        placeholder="Enter guardian's email"
                                                        className="w-full p-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-white/50 focus:ring-2 focus:ring-white/50 transition-all duration-300 text-sm md:text-base"
                                                        required
                                                    />
                                                </div>
                                            )}

                                            {/* Year Group */}
                                            <div>
                                                <label className="block text-white mb-2 text-sm md:text-base">
                                                    <GraduationCap className="w-4 h-4 inline mr-2" />Year Group
                                                </label>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                    {sortedYearGroups.map((year) => (
                                                        <button
                                                            key={year}
                                                            type="button"
                                                            onClick={() => setFormData({ ...formData, yearGroup: year, subjects: [] })}
                                                            className={`p-3 rounded-lg border text-sm md:text-base transition-all duration-300 ${formData.yearGroup === year ? "border-white bg-white/20 text-white" : "border-white/30 text-white/70 hover:border-white/50"}`}
                                                        >
                                                            {year}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Step 3: Subject Selection ── */}
                        {currentStep === 3 && (
                            <div className="space-y-4 md:space-y-6">
                                <h3 className="text-lg md:text-xl font-semibold text-white text-center mb-4 md:mb-6">
                                    <BookOpen className="w-5 h-5 md:w-6 md:h-6 inline mr-2" />
                                    {formData.accountType === "teacher" || formData.accountType === "parent"
                                        ? "Subjects"
                                        : "Pick your subjects (multi-select)"}
                                </h3>

                                {/* PARENT */}
                                {formData.accountType === "parent" && (
                                    formData.children.length === 0 ? (
                                        <div className="text-center py-10 border border-dashed border-white/30 rounded-lg">
                                            <Baby className="w-10 h-10 text-white/40 mx-auto mb-3" />
                                            <p className="text-white font-medium mb-1">No children added</p>
                                            <p className="text-white/60 text-sm">You can add and manage your children from your Parent Dashboard after registration.</p>
                                        </div>
                                    ) : (
                                        formData.children.map((child, childIndex) => (
                                            <div key={childIndex} className="space-y-3 bg-white/5 rounded-lg p-4 border border-white/20">
                                                <h4 className="text-white font-medium mb-2">{child.name}'s Subjects</h4>
                                                <SubjectGrid
                                                    yearGroupKey={child.yearGroup}
                                                    selectedSubjects={child.subjects}
                                                    onToggle={(subject) => handleChildSubjectToggle(childIndex, subject)}
                                                    isTransitioning={isTransitioning}
                                                />
                                                <SubjectSummary subjects={child.subjects} />
                                            </div>
                                        ))
                                    )
                                )}

                                {/* TEACHER */}
                                {formData.accountType === "teacher" && (
                                    formData.students.length === 0 ? (
                                        <div className="text-center py-10 border border-dashed border-white/30 rounded-lg">
                                            <GraduationCap className="w-10 h-10 text-white/40 mx-auto mb-3" />
                                            <p className="text-white font-medium mb-1">No students added</p>
                                            <p className="text-white/60 text-sm">Your Teacher Dashboard has a shareable invite link — students can join your class with one click after you're set up.</p>
                                        </div>
                                    ) : (
                                        formData.students.map((student, studentIndex) => (
                                            <div key={studentIndex} className="space-y-3 bg-white/5 rounded-lg p-4 border border-white/20">
                                                <h4 className="text-white font-medium mb-2">{student.name}'s Subjects</h4>
                                                <SubjectGrid
                                                    yearGroupKey={student.yearGroup}
                                                    selectedSubjects={student.subjects}
                                                    onToggle={(subject) => handleStudentSubjectToggle(studentIndex, subject)}
                                                    isTransitioning={isTransitioning}
                                                />
                                                <SubjectSummary subjects={student.subjects} />
                                            </div>
                                        ))
                                    )
                                )}

                                {/* STUDENT / default */}
                                {formData.accountType !== "parent" && formData.accountType !== "teacher" && (
                                    <div className="subjects-scroll grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 max-h-96 md:max-h-80 overflow-y-auto pr-2">
                                        {filteredSubjects.map((subject, index) => {
                                            const IconComponent = getSubjectIcon(subject);
                                            const isSelected = formData.subjects.includes(subject);
                                            return (
                                                <button
                                                    key={subject}
                                                    type="button"
                                                    onClick={() => handleSubjectToggle(subject)}
                                                    className={`subject-btn p-3 md:p-4 rounded-lg border flex flex-col items-center justify-center space-y-2 text-center ${isSelected ? 'border-white bg-white/20 text-white selected-subject' : 'border-white/30 text-white/70 hover:border-white/50 hover:bg-white/5'}`}
                                                    style={{
                                                        animationDelay: `${index * 50}ms`,
                                                        animation: !isTransitioning ? `fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both` : 'none'
                                                    }}
                                                >
                                                    <IconComponent className={`w-6 h-6 md:w-8 md:h-8 ${isSelected ? 'text-white' : 'text-white/70'}`} />
                                                    <span className={`text-xs md:text-sm font-medium leading-tight ${isSelected ? 'text-white' : 'text-white/80'}`}>{subject}</span>
                                                    {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between mt-6 md:mt-8 gap-4">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 1 || isTransitioning}
                            className={`button-hover flex items-center px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${currentStep === 1 || isTransitioning ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-white/30 text-white hover:bg-white/40'}`}
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                        </button>

                        {currentStep < 3 ? (
                            <button
                                onClick={nextStep}
                                disabled={isTransitioning || (currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)}
                                className={`button-hover flex items-center px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${isTransitioning || ((currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)) ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-white text-[#2148C0] hover:bg-gray-200'}`}
                            >
                                Next <ChevronRight className="w-4 h-4 ml-1" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={!canProceedStep3 || isTransitioning || isLoading}
                                className={`button-hover flex items-center gap-2 px-4 md:px-6 py-2 rounded-lg text-sm md:text-base ${!canProceedStep3 || isTransitioning || isLoading ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-white text-[#2148C0] hover:bg-gray-200'}`}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                        Completing…
                                    </>
                                ) : (
                                    "Complete Registration"
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;